import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Modal, Input, Table, Spin, MenuProps, Typography } from 'antd';
import MapPortalService from '../../../../services/MapPortal/mapportal.service';
import Notification from '../../../../shared/components/Notification';
import { NotificationTypes } from '../../../../enums/notificationTypes';
import useDownload from '../../../../hooks/useDownload';
import { omitNonLayerProperties, useLayerProperitesContext } from '../../../../context/LayerPropertiesContext';
import './ViewAttributeTable.scss';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../../../../enums/queryKeys';
import { Rnd } from 'react-rnd';

// Components
import TableHeader from './components/TableHeader';
import TableFooter from './components/TableFooter';
import AddFieldBox from './components/AddFieldBox';
import MapSelectionModal from './components/MapSelectionModal';
import LayerPropertiesModal from './components/LayerPropertiesModal';
import FileSaveModal from './components/FileSaveModal';
import DownloadDatasetModal from '../DownloadDatasetModal';
import FolderSelectorTree from '../../../../shared/components/FolderSelectorTree';
import { SelectedFolder } from '../../../../types/FileManagerTypes';
import { useForm, Controller } from 'react-hook-form';
import { InputNumber, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { NotificationKeys } from '../../../../enums/notificationKeys';
import ConditionalRender from '../../../../shared/components/ConditionalRender';
import { AiOutlineHolder } from 'react-icons/ai';
import { formatDisplayDate } from '../../../../shared/utils/formatDisplayDate';

const { Title, Text } = Typography;
const MODAL_DEFAULT_WIDTH = 1200;
const MODAL_DEFAULT_HEIGHT = 600;
const MODAL_MIN_WIDTH = 480;
const MODAL_MIN_HEIGHT = 380;
const MODAL_MAX_WIDTH = window.innerWidth - 20;
const MODAL_MAX_HEIGHT = window.innerHeight - 20;
const PAGINATION_RESERVED = 56; // Ant v5 pagination bar height + 8px top margin
// Internal row identity for selection/export/highlight. Must not collide with a
// real attribute named `id` (common in GIS datasets) — overwriting that field
// made the UI show `layer.gid` in the id column and broke search against it.
const FEATURE_ROW_KEY = '__featureId';

interface AttributeFormData {
  features: any[];
  aliases: Record<string, string>;
}

export interface ViewAttributeTableProps {
  isVisible: boolean;
  handleClose: () => void;
  columns?: string[];
  data?: any[];
  layerName: string;
  layerTitle?: string;
  currentMapId?: string | number;
  isQueryTool?: boolean;
  // US-103197/91 (Query result table): first page of matched feature ids
  // ("<layer>.<gid>"). Opens the table in server mode seeded with these as the
  // selection bootstrap. For large results, pair with seedSelectAllMatches.
  seedSelectionIds?: string[];
  /** Total match count from the paginated analysis API (not just the first page). */
  seedMatchTotal?: number;
  /**
   * When true (total matches > first-page ids), seed select-all-matches mode
   * driven by matchFilter/matchExtent/proximityMatch instead of enumerating ids.
   */
  seedSelectAllMatches?: boolean;
  // The COMPACT query predicate (sift filter + GeoJSON extent) used to float
  // matches to the top server-side, and `matchCql` to highlight them on the map
  // — both scale to millions (no enumerated id list anywhere).
  matchFilter?: Record<string, any>;
  matchExtent?: { type: 'Polygon'; coordinates: number[][][] } | null;
  matchCql?: string | null;
  // US-103032 proximity result table: the compact relationship config used to
  // float spatially-matching rows to the top (instead of matchFilter/CQL). When
  // set, the map highlight uses the seeded gid set (no CQL).
  proximityMatch?: Record<string, any> | null;
  onEditQuery?: () => void;
  onResetQuery?: () => void;
  setIsLayerLoading?: (loading: boolean) => void;
  onLayerUpdateComplete?: () => Promise<void>;
  // US-101362 AC-05: only Editor / Admin see the edit-mode pencil.
  canEdit?: boolean;
}

const EditableCell = React.memo(({ control, index, columnName, dataType }: { control: any; index: number; columnName: string; dataType: string }) => {
  return (
    <Controller
      name={`features.${index}.${columnName}`}
      control={control}
      render={({ field }) => {
        const { value, onChange } = field;
        switch (dataType) {
          case 'Integer':
          case 'Float':
            return <InputNumber value={value} onChange={onChange} style={{ width: '100%' }} precision={dataType === 'Integer' ? 0 : undefined} />;
          case 'Date':
            return (
              <DatePicker
                value={value ? dayjs(value) : null}
                format="MM-DD-YYYY"
                onChange={(date) => onChange(date ? date.format('YYYY-MM-DD') : null)}
                style={{ width: '100%' }}
              />
            );
          default:
            return <Input value={value} onChange={onChange} size="small" />;
        }
      }}
    />
  );
});

// Map a PostGIS column type (from the server-driven table) to the editor's
// dataType vocabulary, so EditableCell renders the right input + sorting works.
const pgTypeToDataType = (pgType?: string): string => {
  const t = (pgType || '').toLowerCase();
  if (['integer', 'bigint', 'smallint'].includes(t)) return 'Integer';
  if (['double precision', 'numeric', 'real', 'decimal'].includes(t)) return 'Float';
  if (t.includes('timestamp') || t === 'date') return 'Date';
  return 'Text';
};

// Ant Design Table renders primitives natively; objects/arrays crash React.
// Module-level so column defs can share one stable function identity.
// When `asDate` is true (Date columns), ISO timestamps render as MM-DD-YYYY.
const renderCellValue = (value: unknown, asDate = false): React.ReactNode => {
  if (value == null) return value as null | undefined;
  if (asDate) return formatDisplayDate(value);
  const t = typeof value;
  if (['string', 'number', 'boolean'].includes(t)) return value as string | number | boolean;
  return 'Unsupported Content';
};

const computeDefaultRect = () => {
  const vw = document.documentElement.clientWidth;
  const vh = document.documentElement.clientHeight;
  const offset = 16;
  const width = Math.max(MODAL_MIN_WIDTH, Math.min(MODAL_DEFAULT_WIDTH, vw / 1.5));
  const height = Math.max(MODAL_MIN_HEIGHT, Math.min(MODAL_DEFAULT_HEIGHT, vh / 1.5));

  return { width, height: height - offset, x: 150, y: 150 };
};

const ViewAttributeTable: React.FC<ViewAttributeTableProps> = ({
  isVisible,
  handleClose,
  data: propData,
  layerName,
  layerTitle,
  currentMapId,
  isQueryTool,
  seedSelectionIds,
  seedMatchTotal,
  seedSelectAllMatches,
  matchFilter,
  matchExtent,
  matchCql,
  proximityMatch,
  onEditQuery,
  onResetQuery,
  setIsLayerLoading,
  onLayerUpdateComplete,
  canEdit = true,
}) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableScrollY, setTableScrollY] = useState<number>(400);
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { dirtyFields },
  } = useForm<AttributeFormData>({
    defaultValues: { features: [], aliases: {} },
  });
  // Client (query-tool) mode loads synchronously from propData, so this stays
  // false; server mode uses the query's own fetching state.
  const [loadingData] = useState(false);
  const [fetchedSchema, setFetchedSchema] = useState<{ name: string; dataType: string }[]>([]);

  // Table State
  const [searchTerm, setSearchTerm] = useState('');

  // P1 — server-driven pagination. The grid holds ONE page; page/sort/search
  // are server params so a 50k-feature layer never loads into the browser.
  // The query-tool path (propData) stays client-side.
  const isServerMode = !propData;
  const [page, setPage] = useState(0); // 0-based
  // Infinite scroll: fetch 500 at a time and append as the user scrolls.
  const [pageSize] = useState(500);
  const [totalFeatures, setTotalFeatures] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Tracks the last seeded query selection so a re-run (new matches) re-seeds,
  // but renders with the same matches don't clobber the user's manual changes.
  const seededRef = useRef<string | null>(null);
  // Infinite-scroll latch: prevents a burst of scroll events from advancing the
  // page more than once before the in-flight fetch registers (would skip pages).
  const loadingMoreRef = useRef(false);

  // P3/#1 — "select all matching filter": the selection is the current PREDICATE
  // (search + filters) MINUS an excluded set, not an enumerated id list. Reverse
  // selection swaps between an explicit pick and "all matching except these", so
  // it works at any size. excludedIds holds feature ids deselected within filter
  // mode. Export/save/highlight all consume the predicate (+excluded).
  // Query/Proximity: includedIds holds non-match rows the user manually added to
  // the result (selection = match_pred − excluded ∪ included).
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [excludedIds, setExcludedIds] = useState<string[]>([]);
  const [includedIds, setIncludedIds] = useState<string[]>([]);
  const selectionPredicate = useMemo(
    () => ({
      search: debouncedSearch,
      filters: [] as { field: string; op: string; value: unknown }[],
      excluded: excludedIds,
    }),
    [debouncedSearch, excludedIds],
  );

  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [selectionPage, setSelectionPage] = useState<number | null>(null);
  const [displaySelectionOnly, setDisplaySelectionOnly] = useState(false);
  const [viewActive, setViewActive] = useState(false);
  const [reverseActive, setReverseActive] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [tableData, setTableData] = useState<any[]>([]);
  // const [editedFeatures, setEditedFeatures] = useState<any[]>([]);
  const [creatingAfterFileSave, setCreatingAfterFileSave] = useState(false);

  // #1 — selection display/count derived from the mode. In filter mode the
  // visible checkboxes show "all on this page except the excluded"; the true
  // count is total − excluded.
  // Query/Proximity select-all-matches: only rows with __is_match (from
  // /feature-table) are treated as selected — non-matches stay visible but
  // unchecked so the table can show the full layer with matches on top.
  const watchedFeatures = watch('features') as any[] | undefined;
  const currentPageIds = useMemo(() => (watchedFeatures || []).map((r) => String(r[FEATURE_ROW_KEY])), [watchedFeatures]);
  const currentPageMatchIds = useMemo(
    () =>
      (watchedFeatures || [])
        .filter((r) => r.__is_match === true || r.__is_match === 't' || r.__is_match === 1)
        .map((r) => String(r[FEATURE_ROW_KEY])),
    [watchedFeatures],
  );
  // Query-tool analysis selection uses match rows only; plain table search
  // select-all still uses every row on the page.
  const hasAnalysisMatchPred = Boolean(matchFilter && Object.keys(matchFilter).length) || Boolean(matchExtent) || Boolean(proximityMatch);
  const isQueryMatchSelectAll = Boolean(isQueryTool) && selectAllMatching && hasAnalysisMatchPred;
  const displayedSelectedKeys = useMemo(() => {
    if (!selectAllMatching) return selectedRowKeys;
    if (isQueryMatchSelectAll) {
      // Forward: (matches − excluded) ∪ included
      // Reverse: (non-matches − included) ∪ excluded
      // i.e. every page row that is NOT in the forward selection.
      if (reverseActive) {
        const forwardSelected = new Set([
          ...currentPageMatchIds.filter((id) => !excludedIds.includes(id)),
          ...currentPageIds.filter((id) => includedIds.includes(id)),
        ]);
        return currentPageIds.filter((id) => !forwardSelected.has(id));
      }
      const matchSelected = currentPageMatchIds.filter((id) => !excludedIds.includes(id));
      const extras = currentPageIds.filter((id) => includedIds.includes(id) && !matchSelected.includes(id));
      return [...matchSelected, ...extras];
    }
    return currentPageIds.filter((id) => !excludedIds.includes(id));
  }, [selectAllMatching, isQueryMatchSelectAll, reverseActive, currentPageMatchIds, currentPageIds, excludedIds, includedIds, selectedRowKeys]);
  // Query/Proximity select-all-matches: count is the analysis match total (not
  // the full layer totalFeatures) plus any manually included non-matches.
  // Reverse selects the complement of that set within the full layer.
  const matchSelectionBase = isQueryTool && seedMatchTotal != null && seedMatchTotal > 0 ? seedMatchTotal : totalFeatures;
  const forwardMatchSelectionCount = Math.max(0, matchSelectionBase - excludedIds.length) + (isQueryMatchSelectAll ? includedIds.length : 0);
  const selectionCount = selectAllMatching
    ? isQueryMatchSelectAll && reverseActive
      ? Math.max(0, totalFeatures - forwardMatchSelectionCount)
      : forwardMatchSelectionCount
    : selectedRowKeys.length;
  const hasZeroQueryMatches =
    Boolean(isQueryTool) &&
    !reverseActive &&
    (seedMatchTotal != null ? seedMatchTotal === 0 : (seedSelectionIds?.length ?? 0) === 0) &&
    includedIds.length === 0;

  // Rebuild map highlight CQL when the user edits the analysis selection
  // (exclude a match or include a non-match), or toggles reverse.
  // Forward: (matchCql AND NOT excluded) OR included
  // Reverse: NOT that expression (with empty-match / empty-cql edge cases).
  const publishQuerySelectionHighlight = (nextExcluded: string[], nextIncluded: string[], inverted = reverseActive) => {
    if (!isQueryTool) return;
    const toGids = (ids: string[]) => ids.map((id) => String(id).split('.').pop()).filter((g): g is string => Boolean(g && /^\d+$/.test(g)));
    const excl = toGids(nextExcluded);
    const incl = toGids(nextIncluded);
    let cql: string | null = matchCql ?? null;
    if (cql && excl.length) cql = `(${cql}) AND gid NOT IN (${excl.join(',')})`;
    if (cql && incl.length) cql = `((${cql}) OR gid IN (${incl.join(',')}))`;
    else if (!cql && incl.length) cql = `gid IN (${incl.join(',')})`;

    if (inverted) {
      if (cql) cql = `NOT (${cql})`;
      else if (excl.length) cql = `gid IN (${excl.join(',')})`;
      else cql = null; // reverse of empty selection → nothing highlighted
    }

    layerProperties.setState((prev) => ({
      ...prev,
      selectedFeatureIds: [],
      selectionCql: cql,
    }));
  };

  // Toggle one row honoring the mode: filter mode toggles exclusion (inverted),
  // ids mode toggles the explicit pick (mirrored to context for gid highlight).
  // Query match select-all (forward OR reverse — lists stay in forward terms):
  //   • match row  → toggle excluded (deselect / reselect a match)
  //   • non-match  → toggle included (add / remove from the result set)
  const toggleRowSelection = (id: string) => {
    if (selectAllMatching) {
      if (isQueryMatchSelectAll) {
        const isMatch = currentPageMatchIds.includes(id);
        if (isMatch) {
          setExcludedIds((prev) => {
            const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
            publishQuerySelectionHighlight(next, includedIds);
            return next;
          });
        } else {
          setIncludedIds((prev) => {
            const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
            publishQuerySelectionHighlight(excludedIds, next);
            return next;
          });
        }
        return;
      }
      setExcludedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    } else {
      const next = selectedRowKeys.includes(id) ? selectedRowKeys.filter((x) => x !== id) : [...selectedRowKeys, id];
      setSelectedRowKeys(next);
      layerProperties.setState((prev) => ({ ...prev, selectedFeatureIds: next }));
    }
  };

  // Add Column State
  const [newColumn, setNewColumn] = useState<{ name: string; alias: string; dataType: string | undefined }>({
    name: '',
    alias: '',
    dataType: undefined,
  });
  const [showAddBox, setShowAddBox] = useState(false);
  const [newColumnMetadata, setNewColumnMetadata] = useState<any[]>([]);

  // Modals State
  const [showMapModal, setShowMapModal] = useState(false);
  const [showLayerPropsModal, setShowLayerPropsModal] = useState(false);
  const [allMaps, setAllMaps] = useState<any[]>([]);
  const [selectedMap, setSelectedMap] = useState<number | null>(null);
  const [layerProps, setLayerProps] = useState({ name: '', alias: '' });
  const [showFolderModal, setShowFolderModal] = useState<boolean>(false);
  const [folderId, setFolderId] = useState<string>('');
  const [showFileSaveModal, setShowFileSaveModal] = useState(false);
  const [fileDetails, setFileDetails] = useState({ name: '', type: undefined });
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const [isSaving, setIsSaving] = useState(false);

  // Services
  const {
    editGeoServerFeatures,
    getAllMaps,
    createLayerFromFeatures,
    uploadFeaturesToFolder,
    fetchFeaturesFromLayer,
    getFeatureTable,
    updateFeatureTableRows,
    getFeatureCqlForPredicate,
  } = MapPortalService();
  const { isPending: fetchingFeatures } = useMutation(fetchFeaturesFromLayer());
  const { mutateAsync: updateFeatureTableRowsAsync } = useMutation(updateFeatureTableRows());
  const queryClient = useQueryClient();

  // P1 — one page of the attribute table, filtered/sorted/searched in PostGIS.
  // keepPreviousData (set in the service) keeps the current page visible while
  // the next loads, so paging doesn't flash.
  // US-103191: Display Selection floats analysis matches to the top (or sinks
  // them when reverse is active) — it does NOT hide anything. Driven by the
  // COMPACT query/proximity predicate, not an id list.
  // When Display Selection is OFF we still send the match predicate so
  // `__is_match` stays available for checkboxes, but disable match-first
  // ordering (`matchOrder: false`) so rows return to natural position while
  // remaining selected.
  const orderingParams = useMemo(() => {
    if (!isServerMode || !isQueryTool || !hasAnalysisMatchPred) return {};
    const matchOrder = displaySelectionOnly;
    const matchSink = displaySelectionOnly && reverseActive;
    if (proximityMatch) {
      return { proximityMatch, matchOrder, matchSink };
    }
    if (!matchFilter && !matchExtent) return {};
    return {
      matchFilter,
      matchExtent: matchExtent ?? undefined,
      matchOrder,
      matchSink,
    };
  }, [isServerMode, isQueryTool, hasAnalysisMatchPred, displaySelectionOnly, reverseActive, matchFilter, matchExtent, proximityMatch]);
  // Helper: compact Query/Proximity selection payload for export/save.
  // Under reverse, invert the match predicate server-side (matchInvert) and
  // swap excluded↔included so manual edits stay correct without id lists.
  const buildQueryMatchSelectionPayload = () => {
    const base = proximityMatch ? { proximityMatch } : { matchFilter, matchExtent: matchExtent ?? undefined };
    if (!reverseActive) {
      return {
        ...base,
        ...(excludedIds.length ? { excludedFeatureIds: excludedIds } : {}),
        ...(includedIds.length ? { includedFeatureIds: includedIds } : {}),
      };
    }
    // Reverse of (match − excl ∪ incl) = (¬match − incl ∪ excl)
    // → matchInvert + excluded=included + included=excluded
    return {
      ...base,
      matchInvert: true,
      ...(includedIds.length ? { excludedFeatureIds: includedIds } : {}),
      ...(excludedIds.length ? { includedFeatureIds: excludedIds } : {}),
    };
  };
  const handleRowSelectionChange = (selectedKeys: React.Key[]) => {
    const keys = selectedKeys.map(String);
    if (selectAllMatching) {
      const checked = new Set(keys);
      if (isQueryMatchSelectAll) {
        // Lists stay in FORWARD terms. Under reverse the UI checkboxes show the
        // complement, so reconcile by inverting the checked set first.
        const effectiveChecked = reverseActive ? new Set(currentPageIds.filter((id) => !checked.has(id))) : checked;
        // Matches unchecked (forward) → excluded; non-matches checked (forward) → included.
        // Preserve off-page excluded/included (only reconcile the current page).
        const pageMatchSet = new Set(currentPageMatchIds);
        const pageIdSet = new Set(currentPageIds);
        const nextExcluded = [...excludedIds.filter((id) => !pageMatchSet.has(id)), ...currentPageMatchIds.filter((id) => !effectiveChecked.has(id))];
        const nextIncluded = [
          ...includedIds.filter((id) => !pageIdSet.has(id) || pageMatchSet.has(id)),
          ...currentPageIds.filter((id) => effectiveChecked.has(id) && !pageMatchSet.has(id)),
        ];
        setExcludedIds(nextExcluded);
        setIncludedIds(nextIncluded);
        publishQuerySelectionHighlight(nextExcluded, nextIncluded);
        return;
      }
      // Plain table filter mode: page minus excluded.
      setExcludedIds(currentPageIds.filter((id) => !checked.has(id)));
      return;
    }
    setSelectedRowKeys(keys);
    layerProperties.setState((prev) => ({ ...prev, selectedFeatureIds: keys }));
    setSelectionPage(keys.length > 0 ? page + 1 : null);
  };

  const { data: featureTablePage, isFetching: fetchingPage } = useQuery({
    ...getFeatureTable({ layerName, page, pageSize, search: debouncedSearch, ...orderingParams }),
    enabled: isVisible && !!layerName && isServerMode,
  });
  const { mutateAsync: editGeoServerFeaturesAsync, isPending: editingGeoServerFeatures } = useMutation(editGeoServerFeatures());
  const { mutateAsync: createLayerFromFeaturesAsync, isPending: creatingLayerFromFeatures } = useMutation(createLayerFromFeatures());
  const { mutateAsync: uploadFeaturesToFolderAsync, isPending: uploadingFeaturesToFolder } = useMutation(uploadFeaturesToFolder());
  const {
    data: allMapsResponse,
    isLoading: loadingMaps,
    isFetching: fetchingMaps,
    refetch: refetchMaps,
  } = useQuery({ ...getAllMaps(), enabled: false });
  const { handleAsyncExport, isSelectedDownloadInProgress, isLayerDownloadInProgress } = useDownload({
    customFailureMessage: 'Unable to export results. Please try again.',
  });
  const layerProperties = useLayerProperitesContext();

  // Query-tool path: data is passed in directly (already filtered), stays
  // client-side.
  useEffect(() => {
    if (!isVisible || !propData) return;
    // Keep real attribute `id` intact; WFS feature id lives on FEATURE_ROW_KEY.
    const transformed = propData.map((f: any) => ({ ...f.properties, [FEATURE_ROW_KEY]: f.id }));
    if (propData.length > 0) {
      const keys = Object.keys(propData[0].properties || {});
      setFetchedSchema(keys.map((k) => ({ name: k, dataType: 'Text' })));
    }
    setTableData(transformed);
    reset({ features: transformed, aliases: {} });
  }, [isVisible, propData, reset]);

  // Debounce the search box into the server query (and jump back to page 1).
  // Do NOT clear selectAllMatching for Query/Proximity analysis selection — that
  // mode is seeded from the analysis predicate and must survive the initial
  // empty-search debounce tick (and subsequent table searches keep the seed).
  useEffect(() => {
    if (!isServerMode) return;
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
      // Preserve Query/Proximity analysis select-all; only clear for plain table search.
      if (!(isQueryTool && hasAnalysisMatchPred)) {
        setSelectAllMatching(false);
        setExcludedIds([]);
        setIncludedIds([]);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [searchTerm, isServerMode, isQueryTool, hasAnalysisMatchPred]);

  // Server mode: populate the grid from the current page. The WFS-style feature
  // id (`<layer>.<gid>`) is stored on FEATURE_ROW_KEY so a real attribute named
  // `id` is never overwritten (search/display would otherwise show layer.gid).
  // gid is kept in the row but not shown as a column.
  // The base query (layer + search + ordering); when it changes, infinite
  // scroll restarts from page 0. JSON keys it by VALUE so the object identity of
  // orderingParams doesn't trigger spurious resets.
  const loadKey = useMemo(() => JSON.stringify({ layerName, debouncedSearch, orderingParams }), [layerName, debouncedSearch, orderingParams]);
  useEffect(() => {
    if (!isServerMode) return;
    setPage(0);
  }, [loadKey, isServerMode]);

  useEffect(() => {
    if (!isServerMode || !featureTablePage) return;
    const cols = (featureTablePage.columns || []) as { name: string; type: string }[];
    const schema = cols.filter((c) => c.name.toLowerCase() !== 'gid').map((c) => ({ name: c.name, dataType: pgTypeToDataType(c.type) }));
    setFetchedSchema(schema);
    setTotalFeatures(Number(featureTablePage.total) || 0);

    const rows = (featureTablePage.rows || []).map((r: any) => {
      // Normalize PG boolean / string / int forms of the match flag.
      const rawMatch = r.__is_match;
      const isMatch = rawMatch === true || rawMatch === 't' || rawMatch === 1 || rawMatch === '1';
      const { __is_match: _drop, ...rest } = r;
      return {
        ...rest,
        [FEATURE_ROW_KEY]: `${layerName}.${r.gid}`,
        // Keep a stable boolean for checkbox selection under query match mode.
        ...(rawMatch !== undefined && rawMatch !== null ? { __is_match: isMatch } : {}),
      };
    });
    const aliases = schema.reduce(
      (acc: any, col) => {
        const config = layerProperties.fieldProperties?.find((fp: any) => fp.field === col.name);
        acc[col.name] = config?.alias || col.name;
        return acc;
      },
      {} as Record<string, string>,
    );

    // Page 0 replaces; later pages append (deduped) for infinite scroll.
    const pageNo = Number(featureTablePage.page) || 0;
    if (pageNo === 0) {
      setTableData(rows);
      reset({ features: rows, aliases });
    } else {
      const existing = (watch('features') as any[]) || [];
      const seen = new Set(existing.map((r) => r[FEATURE_ROW_KEY]));
      const merged = [...existing, ...rows.filter((r: any) => !seen.has(r[FEATURE_ROW_KEY]))];
      setTableData(merged);
      reset({ features: merged, aliases });
    }
    loadingMoreRef.current = false; // page settled — allow the next fetch
  }, [featureTablePage, isServerMode, layerName, reset]);

  // Infinite scroll: when the table body nears the bottom and more rows exist,
  // fetch the next page. Listens on the antd scroll container (`.ant-table-body`,
  // which exists in server/non-virtual mode).
  const loadedCount = ((watch('features') as any[]) || []).length;
  const hasMore = isServerMode && loadedCount > 0 && loadedCount < totalFeatures;
  useEffect(() => {
    if (!isServerMode || !isVisible) return;
    const body = tableContainerRef.current?.querySelector('.ant-table-body') as HTMLElement | null;
    if (!body) return;
    const onScroll = () => {
      if (fetchingPage || loadingMoreRef.current || !hasMore) return;
      if (body.scrollTop + body.clientHeight >= body.scrollHeight - 300) {
        loadingMoreRef.current = true;
        setPage((p) => p + 1);
      }
    };
    body.addEventListener('scroll', onScroll, { passive: true });
    return () => body.removeEventListener('scroll', onScroll);
  }, [isServerMode, isVisible, fetchingPage, hasMore]);

  // US-103191/97: seed the query/proximity result selection.
  // ALWAYS use select-all-matches (predicate) mode when there is at least one
  // match and a match predicate is available. Checkboxes then key off
  // `__is_match` from /feature-table (matches selected, non-matches not), which
  // stays correct across infinite-scroll pages without shipping id lists.
  // Map highlight is driven by `selectionCql` (the predicate), NOT id lists.
  // Re-running the query re-seeds (Filter Sc8); plain re-renders don't clobber
  // a user's manual changes.
  useEffect(() => {
    if (!isVisible || !isQueryTool) return;
    if (seedSelectionIds == null && seedMatchTotal == null) return;
    const ids = seedSelectionIds ?? [];
    const total = seedMatchTotal ?? ids.length;
    // Prefer predicate select-all whenever we have matches + a predicate.
    // seedSelectAllMatches forces it for large results; small results use it too
    // so selection survives pagination without relying on a partial id list.
    const useMatchSelectAll = total > 0 && (Boolean(seedSelectAllMatches) || hasAnalysisMatchPred);
    const sig = [...ids].sort().join(',') + '|' + (matchCql ?? '') + '|' + String(total) + '|' + String(useMatchSelectAll);
    if (seededRef.current === sig) return;
    seededRef.current = sig;
    setExcludedIds([]);
    setIncludedIds([]);
    setReverseActive(false);
    setDisplaySelectionOnly(true);
    setViewActive(true);
    setPage(0);
    if (useMatchSelectAll) {
      // Full match set via predicate — checkboxes use __is_match per row.
      // User can still check non-matches to add them (includedIds).
      setSelectedRowKeys([]);
      setSelectAllMatching(true);
    } else if (total === 0) {
      setSelectedRowKeys([]);
      setSelectAllMatching(false);
    } else {
      // Fallback: no predicate available — seed the first-page ids only.
      setSelectedRowKeys(ids);
      setSelectAllMatching(false);
    }
    // Prefer CQL highlight (scales). Fall back to first-page ids only when no CQL.
    if (matchCql) {
      layerProperties.setState((prev) => ({ ...prev, selectedFeatureIds: [], selectionCql: matchCql }));
    } else if (useMatchSelectAll) {
      layerProperties.setState((prev) => ({ ...prev, selectedFeatureIds: [], selectionCql: null }));
    } else {
      layerProperties.setState((prev) => ({ ...prev, selectedFeatureIds: ids, selectionCql: null }));
    }
  }, [isVisible, isQueryTool, seedSelectionIds, seedMatchTotal, seedSelectAllMatches, matchCql, hasAnalysisMatchPred]);

  // --- Handlers ---

  // Clear Selection Handler
  const handleClearSelection = () => {
    setSelectedRowKeys([]);
    setSelectAllMatching(false);
    setExcludedIds([]);
    setIncludedIds([]);
    setReverseActive(false);
    // Clears the map highlight (gid set AND the query predicate CQL).
    setSelectionPage(null);
    layerProperties.setState((prev) => ({
      ...prev,
      selectedFeatureIds: [],
      selectionCql: null,
      highlightColor: null,
    }));
    if (displaySelectionOnly) {
      setDisplaySelectionOnly(false);
      setViewActive(false);
    }
  };

  // #1 Reverse Selection — whole-dataset reverse at any size.
  // Query/Proximity (select-all-matches): stay in predicate mode and flip
  // `reverseActive`. Selection becomes the complement of
  // (matches − excluded ∪ included); Display Selection sinks original matches
  // and floats the opposite set. No id enumeration.
  // Plain attribute table:
  //   • ids mode (N picked) → filter mode, excluded = those N (selects the rest)
  //   • filter mode (all−E) → ids mode, selected = E (selects just those)
  const handleReverseSelection = () => {
    const nextReverse = !reverseActive;

    // Query/Proximity analysis selection: keep predicate mode + flip reverse.
    if (isServerMode && isQueryMatchSelectAll) {
      setReverseActive(nextReverse);
      // Keep Display Selection on so the opposite set floats to the top.
      if (!displaySelectionOnly) {
        setDisplaySelectionOnly(true);
        setViewActive(true);
      }
      setPage(0);
      publishQuerySelectionHighlight(excludedIds, includedIds, nextReverse);
      return;
    }

    if (isServerMode) {
      if (selectAllMatching) {
        const picks = [...excludedIds];
        setSelectAllMatching(false);
        setExcludedIds([]);
        setIncludedIds([]);
        setSelectedRowKeys(picks);
        layerProperties.setState((prev) => ({ ...prev, selectedFeatureIds: picks }));
      } else {
        setExcludedIds([...selectedRowKeys]);
        setIncludedIds([]);
        setSelectedRowKeys([]);
        setSelectAllMatching(true);
        layerProperties.setState((prev) => ({ ...prev, selectedFeatureIds: [] }));
      }
    } else {
      // Client mode: small, in-memory — reverse within the loaded rows.
      const allIds = tableData.map((row) => row[FEATURE_ROW_KEY]);
      const newSelection = allIds.filter((id) => !selectedRowKeys.includes(id));
      layerProperties.setState((prev) => ({ ...prev, selectedFeatureIds: newSelection }));
      setSelectedRowKeys(newSelection);
    }
    setReverseActive(nextReverse);
  };

  const handleToggleViewSelection = () => {
    setDisplaySelectionOnly(!displaySelectionOnly);
    setViewActive(!viewActive);
  };

  // Toggle Edit Handler
  const handleEditClick = () => {
    setIsEditing((prev) => {
      const next = !prev;
      if (!next) {
        setShowAddBox(false);
      }
      return next;
    });
  };

  const handleDownloadClick = (type: string) => {
    // Async server-side export (ogr2ogr → S3), so downloads scale to millions of
    // features. Selection = predicate (all matching), gid list, or whole layer.
    if (isSelectedDownloadInProgress) {
      return;
    } else if (hasZeroQueryMatches) {
      Notification({ message: 'No results available to export.', type: NotificationTypes.WARNING });
      return;
    }
    const format = type as 'shapefile' | 'csv' | 'kml' | 'geojson';
    // Use the current display title so renames from Layer Properties name the file.
    // Data is still resolved by immutable unique layerName.
    const exportOpts = { fileName: layerTitle || layerName };
    // Query/Proximity select-all-matches: export via the analysis predicate so
    // we never ship millions of feature ids. Manual edits (excluded matches /
    // included non-matches) ride along as compact id lists.
    const useQueryMatchPred = isServerMode && selectAllMatching && isQueryTool && hasAnalysisMatchPred;
    if (useQueryMatchPred) {
      handleAsyncExport(layerName, format, buildQueryMatchSelectionPayload(), exportOpts);
    } else if (isServerMode && selectAllMatching) {
      handleAsyncExport(layerName, format, { filter: selectionPredicate }, exportOpts);
    } else if (selectedRowKeys.length > 0) {
      handleAsyncExport(layerName, format, { featureIds: selectedRowKeys }, exportOpts);
    } else {
      handleAsyncExport(layerName, format, {}, exportOpts); // whole layer
    }
  };

  const handleSaveLayer = async () => {
    if (setIsLayerLoading) setIsLayerLoading(true);
    try {
      // Save the FULL selection (held in state across pages), not just the
      // current page's rows — in server mode `tableData` is only one page.
      const useQueryMatchPred = isServerMode && selectAllMatching && isQueryTool && hasAnalysisMatchPred;
      const usePredicate = isServerMode && selectAllMatching && !useQueryMatchPred;
      const selectedFeatures = selectedRowKeys.map((id) => String(id).split('.').pop());
      // Persist only real layer config — never selection/query tool state.
      const propertiesToSave = omitNonLayerProperties(layerProperties);
      const payload = {
        mapId: selectedMap ?? currentMapId,
        layerName: layerProps.name,
        alias: layerProps.alias,
        // Query/Proximity match predicate (+ reverse/manual edits), table search predicate, or gid list.
        ...(useQueryMatchPred ? buildQueryMatchSelectionPayload() : usePredicate ? { filter: selectionPredicate } : { featureIds: selectedFeatures }),
        properties: { ...propertiesToSave, folderId: folderId },
      };
      await createLayerFromFeaturesAsync({ payload });
      if (onLayerUpdateComplete) await onLayerUpdateComplete();
      else if (setIsLayerLoading) setIsLayerLoading(false);
      setShowLayerPropsModal(false);
      setLayerProps({ name: '', alias: '' });
      setSelectedMap(null);
      setCreatingAfterFileSave(false);
    } catch (error) {
      if (setIsLayerLoading) setIsLayerLoading(false);
    }
  };

  const closeFolderModal = () => setShowFolderModal(false);

  // The tree's primary button doubles as the old "Next": pick the folder, then
  // advance to the name/type step. FolderSelectorTree closes itself after this.
  const handleFolderSelect = (folder: SelectedFolder) => {
    setFolderId(folder.id);
    setShowFileSaveModal(true);
  };

  const handleSaveToFolder = async () => {
    if (setIsLayerLoading) setIsLayerLoading(true);
    try {
      // Save the FULL selection (held in state across pages), not just the
      // current page's rows — in server mode `tableData` is only one page.
      const useQueryMatchPred = isServerMode && selectAllMatching && isQueryTool && hasAnalysisMatchPred;
      const usePredicate = isServerMode && selectAllMatching && !useQueryMatchPred;
      const selectedFeatures = selectedRowKeys.map((id) => String(id).split('.').pop());
      const { name, type } = fileDetails;
      // Persist only real layer config — never selection/query tool state.
      const propertiesToSave = omitNonLayerProperties(layerProperties);
      const payload = {
        fileName: name,
        fileType: String(type).toLowerCase(),
        // Query/Proximity match predicate (+ reverse/manual edits), table search predicate, or gid list.
        ...(useQueryMatchPred ? buildQueryMatchSelectionPayload() : usePredicate ? { filter: selectionPredicate } : { featureIds: selectedFeatures }),
        properties: { ...propertiesToSave, folderId: folderId },
      };
      await uploadFeaturesToFolderAsync({ payload });
      setShowFileSaveModal(false);
      setFileDetails({ name: '', type: undefined });
    } catch (error) {
      console.error(error);
    } finally {
      if (!creatingAfterFileSave && setIsLayerLoading) setIsLayerLoading(false);
    }
  };

  useEffect(() => {
    // Closing the table drops any "select all matching" highlight, and lets the
    if (!isVisible) {
      setSelectAllMatching(false);
      seededRef.current = null;
      // Drop the picked folder so the next open starts at Root rather than
      // silently reusing the previous session's target.
      setFolderId('');
    }
  }, [isVisible]);

  // US-103190 Close (Scenario 3) / US-103193 Clear: a single helper used by the
  // result table's Close (X). Blocks while a save/export is mid-flight, then
  // clears the map highlight and closes.
  const operationInFlight = isSaving || creatingLayerFromFeatures || uploadingFeaturesToFolder || isSelectedDownloadInProgress;
  const handleRequestClose = () => {
    if (operationInFlight) {
      Notification({ message: 'Please wait until the current operation is complete.', type: NotificationTypes.WARNING });
      return;
    }
    if (isQueryTool) {
      // Remove all query-result highlights/selection graphics from the map.
      layerProperties.setState((prev) => ({ ...prev, selectedFeatureIds: [], selectionCql: null, highlightColor: null }));
    }
    handleClose();
  };

  // While a download is polling OR any save modal is open, the toolbar actions
  // (Filter, Clear, Interchange, Download, Save) stay clickable but are
  // intercepted: the action is skipped and a warning is shown instead.
  const downloadInProgress = isSelectedDownloadInProgress || isLayerDownloadInProgress;
  const saveModalOpen = showMapModal || showLayerPropsModal || showFolderModal || showFileSaveModal || showDownloadModal;
  const actionsLocked = downloadInProgress || saveModalOpen;
  const runGuarded = (action: () => void) => {
    if (actionsLocked) {
      Notification({ message: 'Please wait until the current operation is complete.', type: NotificationTypes.WARNING });
      return;
    }
    action();
  };

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    let rafId: number;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const thead = el.querySelector<HTMLElement>('.ant-table-header');
        const theadH = thead?.offsetHeight ?? 40;
        const paginationH = isServerMode ? PAGINATION_RESERVED : 0;
        setTableScrollY(Math.max(100, el.clientHeight - theadH - paginationH));
      });
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [isServerMode]);

  // P4 — publish the highlight predicate as a CQL_FILTER so the map renders a
  // any size). Cleared when the mode turns off → falls back to gid-based.
  useEffect(() => {
    // Query mode manages its own highlight via the predicate CQL (matchCql) in
    // the seed / interchange / clear handlers — don't let the attribute-table
    // path clobber it.
    if (!isServerMode || isQueryTool) return;
    let cancelled = false;
    if (selectAllMatching) {
      getFeatureCqlForPredicate(layerName, selectionPredicate)
        .then((cql) => {
          if (!cancelled) layerProperties.setState((prev) => ({ ...prev, selectionCql: cql || null }));
        })
        .catch(() => undefined);
    } else {
      layerProperties.setState((prev) => ({ ...prev, selectionCql: null }));
    }
    return () => {
      cancelled = true;
    };
  }, [selectAllMatching, selectionPredicate, isServerMode, layerName, isQueryTool]);

  // Bail before opening the modal so the user isn't asked to pick a format for
  // an export that can't produce anything.
  const handleShowDownload = () =>
    runGuarded(() => {
      if (hasZeroQueryMatches) {
        Notification({ message: 'No results available to export.', type: NotificationTypes.WARNING });
        return;
      }
      setShowDownloadModal(true);
    });

  const saveMenu: MenuProps['items'] = [
    {
      key: 'saveInMap',
      label: 'SAVE SELECTION IN MAP',
      onClick: () =>
        runGuarded(async () => {
          setShowMapModal(true);
          const response = await refetchMaps();
          setAllMaps(response.data || allMapsResponse || []);
        }),
    },
    {
      key: 'saveInFileManager',
      label: 'SAVE SELECTION IN FILE MANAGER',
      onClick: () => runGuarded(() => setShowFolderModal(true)),
    },
  ];

  const modalClassName = ['viewAttributeTableModal', isQueryTool ? 'viewAttributeTableModal--query' : ''].filter(Boolean).join(' ');

  const combinedSchema = useMemo(() => {
    return [...fetchedSchema, ...newColumnMetadata];
  }, [fetchedSchema, newColumnMetadata]);

  const handleAddColumn = () => {
    if (!newColumn.name || !newColumn.dataType) {
      Notification({ message: 'Field name and Data Type are required', type: NotificationTypes.ERROR });
      return;
    }

    const newColName = newColumn.name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_]/gi, '_');

    if (newColName.length > 63) {
      Notification({
        message: 'Field name is too long',
        description: 'Field name cannot exceed 63 characters.',
        type: NotificationTypes.ERROR,
      });
      return;
    }

    const exists = combinedSchema.some((c) => c.name === newColName);

    if (exists) {
      Notification({ message: 'Column already exists', type: NotificationTypes.ERROR });
      return;
    }

    setNewColumnMetadata((prev) => [
      ...prev,
      {
        name: newColName,
        alias: newColumn.alias || newColumn.name,
        dataType: newColumn.dataType,
      },
    ]);

    setTableData((prev) => prev.map((row) => ({ ...row, [newColName]: '' })));

    setNewColumn({ name: '', alias: '', dataType: undefined });
    setShowAddBox(false);
  };

  const tableColumns = useMemo(() => {
    return combinedSchema.map((colObj) => {
      const colName = colObj.name;
      const dataType = colObj.dataType;

      const isSavedColumn = fetchedSchema.some((c) => c.name === colName);

      return {
        title: <div className="columnHeader">{colName}</div>,
        dataIndex: colName,
        key: colName,
        width: 180,
        render: (text: any, _: any, index: number) => {
          // Edit mode uses typed inputs; read mode only allows Ant Design-safe primitives.
          // Date columns format API timestamps (e.g. 2024-03-15T00:00:00.000Z) as MM-DD-YYYY.
          return isEditing ? (
            <EditableCell control={control} index={index} columnName={colName} dataType={dataType} />
          ) : (
            renderCellValue(text, dataType === 'Date')
          );
        },
        sorter: isSavedColumn
          ? (a: any, b: any) => {
              const valA = a[colName];
              const valB = b[colName];

              // 1. Handle identical values
              if (valA === valB) return 0;

              // 2. Handle null/undefined values (pushed to the end)
              if (valA === null || valA === undefined || valA === '') return 1;
              if (valB === null || valB === undefined || valB === '') return -1;

              // 3. Type-specific sorting
              switch (dataType) {
                case 'Integer':
                case 'Float':
                  return Number(valA) - Number(valB);

                case 'Date':
                  // Using .valueOf() is faster than string comparison for dates
                  return dayjs(valA).valueOf() - dayjs(valB).valueOf();

                case 'Text':
                default:
                  // localeCompare with 'numeric: true' handles strings like "Layer 2" vs "Layer 10" correctly
                  return String(valA).localeCompare(String(valB), undefined, {
                    numeric: true,
                    sensitivity: 'base',
                  });
              }
            }
          : false,
      };
    });
  }, [combinedSchema, isEditing, control]);

  const handleSave = handleSubmit(async (formData) => {
    try {
      setIsSaving(true);
      setIsLayerLoading?.(true);

      // 1. Identify only the rows that were actually modified
      const updates: any[] = [];
      if (dirtyFields.features) {
        Object.keys(dirtyFields.features).forEach((key) => {
          const index = Number(key);
          const featureData = formData.features[index];
          if (featureData) {
            // FEATURE_ROW_KEY is UI-only; keep real attribute `id` in properties.
            const { [FEATURE_ROW_KEY]: featureId, ...properties } = featureData;
            updates.push({ id: featureId, properties });
          }
        });
      }

      const hasPropertyUpdates = updates.length > 0;
      const hasNewColumns = newColumnMetadata.length > 0;
      const hasAliasUpdates = !!dirtyFields.aliases;

      if (!hasPropertyUpdates && !hasNewColumns && !hasAliasUpdates) {
        Notification({ message: 'No changes detected', type: NotificationTypes.INFO, key: NotificationKeys.NO_CHANGES_DETECTED });
        setIsEditing(false);
        handleClose();
        return;
      }

      if (isServerMode) {
        // P2 — gid-keyed UPDATE straight into PostGIS (+ ALTER TABLE for new
        // columns), scales for bulk edits. gid is read off the row; it's
        // stripped from the writable properties.
        const serverUpdates = updates.map((u) => {
          const { gid, ...properties } = u.properties ?? {};
          return { gid: gid ?? String(u.id).split('.').pop(), properties };
        });
        await updateFeatureTableRowsAsync({ layerName, updates: serverUpdates, newColumns: newColumnMetadata });
        // Refetch the visible page so saved values + any new columns show.
        queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_FEATURE_TABLE] });
      } else {
        await editGeoServerFeaturesAsync({
          layerName,
          payload: { features: updates, newColumns: newColumnMetadata },
        });
      }

      if (hasNewColumns) {
        setFetchedSchema((prev) => [...prev, ...newColumnMetadata]);
        // Query/Proximity attribute dropdowns are driven by these caches. The BE
        // now appends new columns to query/proximity_selected_columns, but the
        // client must drop the stale mapping/schema responses or the tools keep
        // showing the pre-save field list until a full reload.
        queryClient.invalidateQueries({ queryKey: [QueryKeys.FETCH_FEATURES_KEY_VALUE_MAPPING] });
        queryClient.invalidateQueries({ queryKey: [QueryKeys.SYMBOLOGY_FIELDS] });
      }

      reset({
        features: formData.features,
        aliases: formData.aliases,
      });

      if (hasAliasUpdates) {
        const updatedFieldProps = Object.entries(formData.aliases).map(([field, alias]) => ({
          field,
          alias,
          visibility: true,
          format: 'Auto Detect',
        }));
        layerProperties.setState((prev) => ({
          ...prev,
          fieldProperties: updatedFieldProps,
        }));
      }

      Notification({ message: 'Changes saved successfully', type: NotificationTypes.SUCCESS });

      setNewColumnMetadata([]);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save changes:', error);
      Notification({ message: 'Save failed. Data may be out of sync.', type: NotificationTypes.ERROR });
    } finally {
      setIsSaving(false);
      setIsLayerLoading?.(false);
      setShowAddBox(false);
    }
  });

  const filteredRowData = useMemo(() => {
    let result = watch('features') || [];

    // Server mode filters in PostGIS; only the client (query-tool) path searches
    // the in-memory rows here. Skip the internal row key so it never matches.
    if (!isServerMode && searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter((item: any) =>
        Object.entries(item).some(([key, value]) => key !== FEATURE_ROW_KEY && String(value).toLowerCase().includes(q)),
      );
    }

    // "View selected only": the Query result table reorders server-side
    // (selected float to the top, nothing hidden), so it must NOT filter here.
    // The attribute table keeps its current-page "show selected" filter.
    if (!isQueryTool && displaySelectionOnly) {
      result = result.filter((item: any) => selectedRowKeys.includes(item[FEATURE_ROW_KEY]));
    }

    return result;
  }, [watch('features'), searchTerm, displaySelectionOnly, selectedRowKeys, isQueryTool]);

  return (
    <>
      <Modal
        open={isVisible}
        onCancel={handleRequestClose}
        mask={false}
        maskClosable={false}
        footer={
          <ConditionalRender visible={isQueryTool || canEdit}>
            <TableFooter
              isQueryTool={isQueryTool}
              onEditQuery={onEditQuery}
              onResetQuery={onResetQuery}
              onSaveAndClose={handleSave}
              isSaving={isSaving}
              canEdit={canEdit}
            />
          </ConditionalRender>
        }
        title={
          <>
            <AiOutlineHolder className="modal-drag-holder" />
            <div className="titleSection">
              <Title level={5} className="vat-header__title">
                {layerTitle ?? 'Layer Name'}
              </Title>
              <Text type="secondary">
                {totalFeatures} FEATURES
                <ConditionalRender visible={selectionCount > 0}>
                  <> | {selectionCount} SELECTED</>
                </ConditionalRender>
              </Text>
            </div>
          </>
        }
        modalRender={(modal) => (
          <Rnd
            default={computeDefaultRect()}
            minHeight={MODAL_MIN_HEIGHT}
            minWidth={MODAL_MIN_WIDTH}
            maxHeight={MODAL_MAX_HEIGHT}
            maxWidth={MODAL_MAX_WIDTH}
            dragHandleClassName="modal-drag-holder"
            cancel=".ant-modal-close"
            enableResizing
            bounds="window"
            style={{ zIndex: 1001 }}
            resizeHandleClasses={{
              bottom: 'rnd-handle',
              bottomRight: 'rnd-handle',
              bottomLeft: 'rnd-handle',
              right: 'rnd-handle',
              left: 'rnd-handle',
              top: 'rnd-handle',
              topRight: 'rnd-handle',
              topLeft: 'rnd-handle',
            }}
          >
            {modal}
          </Rnd>
        )}
        className={modalClassName}
      >
        <TableHeader
          isQueryTool={isQueryTool}
          viewActive={viewActive}
          reverseActive={reverseActive}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          isEditing={isEditing}
          hasSelection={selectionCount > 0}
          onClearSelection={() => runGuarded(handleClearSelection)}
          onReverseSelection={() => runGuarded(handleReverseSelection)}
          onToggleEdit={handleEditClick}
          onShowAddColumn={() => setShowAddBox(!showAddBox)}
          onShowDownload={handleShowDownload}
          saveMenu={saveMenu}
          onToggleViewSelection={() => runGuarded(handleToggleViewSelection)}
          canEdit={canEdit}
        />

        {showAddBox && <AddFieldBox newColumn={newColumn} setNewColumn={setNewColumn} onSave={handleAddColumn} />}

        {/* P3 — bridge per-page selection to a whole-dataset predicate so
            export/save can act on all matching features without enumerating ids. */}
        {isServerMode && (selectAllMatching || (selectedRowKeys.length > 0 && totalFeatures > selectedRowKeys.length)) && (
          <div className="vat-select-all-bar">
            {selectAllMatching ? (
              <>
                <b>{selectionCount}</b> of{' '}
                {isQueryMatchSelectAll ? (reverseActive ? totalFeatures : matchSelectionBase + includedIds.length) : totalFeatures} features selected
                for export/save
                {reverseActive ? ' (reversed)' : ''}
                {!reverseActive && excludedIds.length > 0 ? ` (${excludedIds.length} deselected)` : ''}
                {!reverseActive && includedIds.length > 0 ? ` (${includedIds.length} added)` : ''}.{' '}
                <a onClick={handleClearSelection}>Clear selection</a>
              </>
            ) : (
              <>
                <b>{selectedRowKeys.length}</b> selected on page <b>{selectionPage}</b>.{' '}
                <a
                  onClick={() => {
                    setExcludedIds([]);
                    setSelectAllMatching(true);
                    setSelectedRowKeys([]);
                    layerProperties.setState((prev) => ({ ...prev, selectedFeatureIds: [] }));
                  }}
                >
                  Select all {totalFeatures} matching
                </a>
              </>
            )}
          </div>
        )}

        <div className="tableContainer" ref={tableContainerRef}>
          {/* Full-screen spinner only before the first page arrives; subsequent
              page loads use the Table's own `loading` so rows don't unmount
              (no flash, thanks to keepPreviousData). */}
          {(isServerMode ? fetchingPage && !featureTablePage : loadingData) ? (
            <div className="vat-loading">
              <Spin />
            </div>
          ) : (
            <Table
              virtual={!isServerMode}
              // Initial load uses the full overlay (above); page appends show a
              // subtle footer spinner instead, so loaded rows don't unmount.
              loading={isServerMode && fetchingPage && loadedCount === 0}
              // US-103168 Scenario 17: the Query Tool result table shows a
              // clear "No results found." message when a query matches nothing.
              locale={isQueryTool ? { emptyText: 'No results found.' } : undefined}
              scroll={{ y: tableScrollY, x: 'max-content' }}
              rowKey={FEATURE_ROW_KEY}
              dataSource={filteredRowData}
              columns={tableColumns}
              bordered
              // Infinite scroll (server mode): no pager — rows are appended as
              // the user scrolls (see the scroll effect). The footer shows
              // progress / a "load more" spinner.
              pagination={false}
              footer={
                isServerMode
                  ? () => (
                      <div className="vat-infinite-footer">
                        {fetchingPage && loadedCount > 0 ? (
                          <span>
                            <Spin size="small" /> Loading more…
                          </span>
                        ) : (
                          <span>
                            {loadedCount} of {totalFeatures} features
                          </span>
                        )}
                      </div>
                    )
                  : undefined
              }
              className="custom-table bordered-table"
              rowSelection={
                !isEditing
                  ? {
                      // Mode-aware: ids mode shows the explicit picks; filter mode
                      // shows "this page minus excluded". The query result table
                      // (US-103197) uses the same checkbox selection model.
                      selectedRowKeys: displayedSelectedKeys,
                      onChange: handleRowSelectionChange,
                      columnWidth: 30,
                    }
                  : undefined
              }
              // US-101362 AC-06: clicking anywhere on the row toggles its
              // selection so the feature highlights on the map. Skipped while
              // editing so cell interactions still work.
              onRow={(record) =>
                !isEditing
                  ? {
                      onClick: () => toggleRowSelection(String(record[FEATURE_ROW_KEY])),
                      style: { cursor: 'pointer' },
                    }
                  : {}
              }
              rowClassName={(record) => (displayedSelectedKeys.includes(String(record[FEATURE_ROW_KEY])) ? 'selected-row' : '')}
            />
          )}
        </div>
      </Modal>

      {/* Sub Modals */}
      <MapSelectionModal
        visible={showMapModal}
        onCancel={() => {
          setShowMapModal(false);
          setSelectedMap(null);
        }}
        loadingMaps={fetchingMaps}
        allMaps={allMaps}
        selectedMap={selectedMap}
        setSelectedMap={setSelectedMap}
        currentMapId={currentMapId}
        onNext={() => {
          setShowMapModal(false);
          setShowLayerPropsModal(true);
        }}
      />

      <LayerPropertiesModal
        visible={showLayerPropsModal}
        onCancel={() => {
          setShowLayerPropsModal(false);
          setLayerProps({ name: '', alias: '' });
          setSelectedMap(null);
        }}
        onSave={handleSaveLayer}
        layerProps={layerProps}
        setLayerProps={setLayerProps}
        isLoading={fetchingFeatures || editingGeoServerFeatures}
      />

      <FolderSelectorTree
        open={showFolderModal}
        onClose={closeFolderModal}
        onSelect={handleFolderSelect}
        variant="light"
        title="SELECT FOLDER"
        primaryLabel="NEXT"
        initialFolderId={folderId}
      />

      <FileSaveModal
        visible={showFileSaveModal}
        onCancel={() => {
          setShowFileSaveModal(false);
          setFileDetails({ name: '', type: undefined });
        }}
        fileDetails={fileDetails}
        setFileDetails={setFileDetails}
        onSave={handleSaveToFolder}
        onSaveAndAdd={async () => {
          // Re-enter Flow A after saving the dataset: open the Map List, then
          // continue Map List -> Layer Properties -> save-to-map (reuses Flow A).
          setCreatingAfterFileSave(true);
          await handleSaveToFolder();
          setShowMapModal(true);
          const response = await refetchMaps();
          setAllMaps(response.data || allMapsResponse || []);
        }}
        isLoading={creatingLayerFromFeatures || uploadingFeaturesToFolder || loadingMaps}
      />

      {showDownloadModal && (
        <DownloadDatasetModal
          open={showDownloadModal}
          title="DOWNLOAD DATASET"
          onClose={() => setShowDownloadModal(false)}
          onDownload={handleDownloadClick}
        />
      )}
    </>
  );
};

export default ViewAttributeTable;
